import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot,
  ArrowLeft,
  Settings,
  MessageCircle,
  MessageSquare,
  User,
  CheckCircle,
  AlertCircle,
  Copy,
  Eye,
  EyeOff,
  QrCode,
  Clock,
  Smartphone,
  Loader2,
  Wifi,
  WifiOff,
  Radio,
} from "lucide-react";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import logo from "../assets/i2.png";
import { Tooltip } from "react-tooltip";
import SingleMessage from "../components/Instances/SingleMessage";
import SafeModeSwitch from "../components/SafeModeSwitch";
import ConfigurationTab from "../pages/ConfigurationTab";
import { TagProvider } from "../contexts/TagContext";
import { MessageProvider } from "../contexts/MessageContext";

const API_BASE = process.env.REACT_APP_API_URL;
const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_SERVER_URL;

const languageOptions = [
  { value: "en", label: "English" },
  { value: "gj", label: "Gujarati" },
  { value: "hn", label: "Hindi" },
  { value: "hnen", label: "Hinglish" },
];
const InstancePage = () => {
  const { instanceId } = useParams();
  const navigate = useNavigate();

  // Token & user info
  const [authToken, setAuthToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [username, setUsername] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [instanceName, setInstanceName] = useState("");
  const [safeMode, setSafeMode] = useState(false);

  // Socket and connection states
  const [socket, setSocket] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState("Checking...");
  const [statusDetails, setStatusDetails] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [lastQrTime, setLastQrTime] = useState(null);
  const [connectionInfo, setConnectionInfo] = useState({
    socketConnected: false,
    instanceReady: false,
  });

  const [knowledgeBase, setKnowledgeBase] = useState("");
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [replyGroup, setReplyGroup] = useState("");

  const [ai_reply_days, setAiReplyDays] = useState([]);
  const [ai_reply_start_time, setAiReplyStartTime] = useState("");
  const [ai_reply_end_time, setAiReplyEndTime] = useState("");
  const [ai_timezone, setAiTimezone] = useState("");

  const [language, setLanguage] = React.useState([]); // array
  const [role, setRole] = React.useState(""); // text
  const [tone, setTone] = React.useState(""); // text
  const [outOfContextReply, setOutOfContextReply] = useState("");

  const [tasksAndRules, setTasksAndRules] = useState("");
  const [voiceTranscription, setVoiceTranscription] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEnabled, setWebhookEnabled] = useState(false);

  const [replyToBoardcastOnly, setReplyToBoardcastOnly] =
    React.useState(false); // text
  const [isGeneratingConfig, setIsGeneratingConfig] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    sent: 0,
    queue: 0,
    unsent: 0,
    invalid: 0,
    expired: 0,
  });

  // Main tabs state
  const [activeTab, setActiveTab] = useState("configuration");

  // Loading states for QR scanning
  const [qrLoading, setQrLoading] = useState(false);
  const [scanningLoader, setScanningLoader] = useState(false);

  // Fetch instance data and token
  const fetchInstanceData = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/instances/instanceData/${instanceId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      if (data.success && data.data) {
        setAuthToken(data.data[0].token);
        setTokenExpiry(data.data[0].expires_at);
        setUsername(data.data[0].username);
        setChatbotEnabled(!!data.data[0].ai_chatbot);
        setKnowledgeBase(data.data[0].knowledge_base || "");
        setReplyGroup(data.data[0].reply_group || "");
        setLanguage(data.data[0].language || "");
        setRole(data.data[0].role || "");
        setTone(data.data[0].tone || "");
        setSafeMode(!!data.data[0].safe_mode);
        setReplyToBoardcastOnly(!!data.data[0].reply_to_broadcast_only);
        setAiReplyDays(data.data[0].ai_reply_days || []);
        setAiReplyStartTime(data.data[0].ai_reply_start_time || "");
        setAiReplyEndTime(data.data[0].ai_reply_end_time || "");
        setAiTimezone(data.data[0].ai_timezone || "");
        setOutOfContextReply(data.data[0].out_of_context_reply || "");
        setTasksAndRules(data.data[0].tasks_and_rules || "");
        setVoiceTranscription(data.data[0].voice_transcription);
        setWebhookUrl(data.data[0].webhook_url || "");
        setWebhookEnabled(!!data.data[0].webhook_enabled);
      } else {
        toast.error("Failed to fetch authentication token");
        navigate("/main");
      }
    } catch (error) {
      console.error("Failed to fetch instance data:", error);
      toast.error("Error fetching instance data");
    }
  };

  // --- New API: Save Knowledge Base ---
  const saveKnowledgeBase = async (knowledgeBase) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/knowledge-base`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ knowledge_base: knowledgeBase }),
        }
      );

      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Knowledge base saved successfully");
      } else {
        toast.error(data.error || "Failed to save knowledge base");
      }
    } catch (err) {
      console.error("Knowledge Base Error:", err);
      toast.error("Error saving knowledge base");
    }
  };

  // --- New API: Toggle Chatbot ---
  const toggleChatbot = async (nextValue) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/chatbot-toggle`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ ai_chatbot: nextValue ? 1 : 0 }),
        }
      );

      const data = await res.json();
      if (res.ok && data.success) {
        setChatbotEnabled(!!data.data.ai_chatbot);
        toast.success(data.message || "Chatbot setting updated");
      } else {
        setChatbotEnabled((prev) => !prev);
        toast.error(data.error || "Failed to update chatbot setting");
      }
    } catch (err) {
      console.error("Chatbot Toggle Error:", err);
      setChatbotEnabled((prev) => !prev);
    }
  };
  // --- New API: Toggle Reply Group ---
  const toggleReplyGroup = async (nextGroup) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/reply-group`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ reply_group: nextGroup }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setReplyGroup(data.data.reply_group);
        toast.success(data.message || "Reply group updated");
      } else {
        toast.error(data.error || "Failed to update reply group");
      }
    } catch (error) {
      console.error("Reply Group Error:", error);
      toast.error("Error updating reply group");
    }
  };

  const toggleVoiceTranscription = async (nextValue) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/voice-transcription`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ voice_transcription: nextValue }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setVoiceTranscription(data.data.voice_transcription);
        toast.success(data.message || "Voice transcription setting updated");
      } else {
        toast.error(data.message || "Failed to update voice transcription");
      }
    } catch (error) {
      console.error("Voice Transcription Error:", error);
      toast.error("Error updating voice transcription");
    }
  };

  const updateLanguage = async (language) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/language`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({
            language,
          }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setLanguage(language);
        toast.success(data.message || "Language updated successfully");
      } else {
        toast.error(data.error || "Failed to update language");
      }
    } catch (error) {
      console.error("Language Update Error:", error);
      toast.error("Error updating language");
    }
  };

  const updateRole = async (role) => {
    try {
      const res = await fetch(`${API_BASE}/api/instances/${instanceId}/role`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
        },
        body: JSON.stringify({ role }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRole(data.data.role);
        toast.success(data.message || "Role updated successfully");
      } else {
        toast.error(data.error || "Failed to update role");
      }
    } catch (error) {
      console.error("Role Update Error:", error);
      toast.error("Error updating role");
    }
  };

  const updateTone = async (tone) => {
    try {
      const res = await fetch(`${API_BASE}/api/instances/${instanceId}/tone`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
        },
        body: JSON.stringify({ tone }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setTone(data.data.tone);
        toast.success(data.message || "Tone updated successfully");
      } else {
        toast.error(data.error || "Failed to update tone");
      }
    } catch (error) {
      console.error("Tone Update Error:", error);
      toast.error("Error updating tone");
    }
  };

  const updateOutOfContextReply = async (outOfContextReply) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/out_of_context_reply`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ out_of_context_reply: outOfContextReply }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setOutOfContextReply(data.data.out_of_context_reply);
        toast.success(
          data.message || "Out of Context Reply updated successfully"
        );
      } else {
        toast.error(data.error || "Failed to update out of context reply");
      }
    } catch (error) {
      console.error("OutOfContextReply Update Error:", error);
      toast.error("Error updating out of context reply");
    }
  };

  // updated reply to broadcast only
  const updateReplyToBroadcastOnly = async (nextValue) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/reply_to_broadcast_only`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({
            reply_to_broadcast_only: nextValue ? 1 : 0,
          }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setReplyToBoardcastOnly(!!data.data.reply_to_broadcast_only);
        toast.success(
          data.message || "Reply to broadcast only updated successfully"
        );
      } else {
        toast.error(data.error || "Failed to update reply to broadcast only");
      }
    } catch (error) {
      console.error("Reply to Broadcast Only Update Error:", error);
      toast.error("Error updating reply to broadcast only");
    }
  };

  const updateAiReplyScheduleAPI = async (scheduleData) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/ai-reply-schedule`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify(scheduleData),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        // Update local state with saved values
        setAiReplyDays(scheduleData.ai_reply_days);
        setAiReplyStartTime(scheduleData.ai_reply_start_time);
        setAiReplyEndTime(scheduleData.ai_reply_end_time);
        setAiTimezone(scheduleData.ai_time_zone);

        toast.success(data.message || "AI reply schedule updated successfully");
      } else {
        toast.error(data.error || "Failed to update AI reply schedule");
      }
    } catch (error) {
      console.error("AI Reply Schedule Update Error:", error);
      toast.error("Error updating AI reply schedule");
    }
  };

  const updateTasksAndRules = async (tasksAndRulesData) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/tasks-and-rules`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ tasks_and_rules: tasksAndRulesData }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        // Update local state with saved value
        setTasksAndRules(tasksAndRulesData);

        toast.success(data.message || "Tasks and rules updated successfully");
      } else {
        toast.error(data.error || "Failed to update tasks and rules");
      }
    } catch (error) {
      console.error("Tasks and Rules Update Error:", error);
      toast.error("Error updating tasks and rules");
    }
  };

  const updateWebhookUrl = async (webhookUrlData) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/webhook-url`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ webhook_url: webhookUrlData }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        // Update local state with saved value
        setWebhookUrl(data.data?.webhook_url || webhookUrlData || "");

        toast.success(data.message || "Webhook URL updated successfully");
      } else {
        toast.error(data.error || "Failed to update webhook URL");
      }
    } catch (error) {
      console.error("Webhook URL Update Error:", error);
      toast.error("Error updating webhook URL");
    }
  };

  const updateWebhookEnabled = async (enabled) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/webhook-enabled`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ webhook_enabled: enabled }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        // Update local state with saved value
        setWebhookEnabled(enabled);

        toast.success(data.message || "Webhook enabled status updated successfully");
      } else {
        toast.error(data.error || "Failed to update webhook enabled status");
      }
    } catch (error) {
      console.error("Webhook Enabled Update Error:", error);
      toast.error("Error updating webhook enabled status");
    }
  };

  const generateConfigFromWebsite = async (websiteUrl) => {
    if (!websiteUrl) {
      return { success: false, message: "Website URL is required" };
    }

    setIsGeneratingConfig(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/generate-config-from-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ url: websiteUrl }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        const generated = data.data || {};
        setKnowledgeBase(generated.knowledge_base || "");
        setTasksAndRules(generated.tasks_and_rules || "");
        toast.success(
          data.message || "Configuration generated from website content"
        );
        return { success: true, data: generated, message: data.message };
      }

      toast.error(data.message || data.error || "Failed to generate config");
      return { success: false, message: data.message || data.error };
    } catch (error) {
      console.error("Generate config error:", error);
      toast.error("Error generating configuration from website");
      return { success: false, message: error.message };
    } finally {
      setIsGeneratingConfig(false);
    }
  };

  const updateInstanceName = async (name) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/name`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ name }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        setInstanceName(data.data.name);
        toast.success(data.message || "Instance name updated successfully");
      } else {
        toast.error(data.error || "Failed to update instance name");
      }
    } catch (error) {
      console.error("Instance Name Update Error:", error);
      toast.error("Error updating instance name");
    }
  };


  // Fetch WhatsApp status from API
  const fetchWhatsAppStatus = async () => {
    if (!authToken) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/instances/status/${instanceId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setInstanceName(data.data.name);
        setSafeMode(data.data.safe_mode);
        setIsLoggedIn(data.data.isWhatsappLogin);
        setWhatsappStatus(
          data.data.isWhatsappLogin ? "LOGGED IN" : "NOT LOGGED IN"
        );
        setStatusDetails(data.message || "Status retrieved successfully");
      }
    } catch (error) {
      setWhatsappStatus("ERROR");
      setStatusDetails("Failed to fetch WhatsApp status");
    }
  };

  // Initialize socket connection
  const initializeSocket = () => {
    if (!authToken) return;

    if (socket) {
      socket.disconnect();
    }

    setWhatsappStatus("Connecting to server...");

    const newSocket = io(SOCKET_SERVER_URL, {
      auth: { token: authToken, instanceId },
      autoConnect: true,
      timeout: 20000,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on("connect", () => {
      setConnectionInfo((prev) => ({ ...prev, socketConnected: true }));
      setWhatsappStatus("CONNECTED");
      setStatusDetails("Connected to server successfully");
      newSocket.emit("connect_instance", { instanceId });
      fetchWhatsAppStatus();
    });

    newSocket.on("connect_error", (error) => {
      setWhatsappStatus("CONNECTION ERROR");
      setStatusDetails(`Connection failed: ${error.message}`);
      setConnectionInfo((prev) => ({ ...prev, socketConnected: false }));
    });

    newSocket.on("disconnect", (reason) => {
      setConnectionInfo((prev) => ({ ...prev, socketConnected: false }));
      setWhatsappStatus("DISCONNECTED");
      setStatusDetails(`Connection lost: ${reason}`);
    });

    newSocket.on("instance_status", (data) => {
      const { isWhatsappLogin, message } = data;
      setIsLoggedIn(isWhatsappLogin);
      setWhatsappStatus(isWhatsappLogin ? "LOGGED IN" : "NOT LOGGED IN");
      setStatusDetails(message || "Status updated");
      setConnectionInfo((prev) => ({
        ...prev,
        instanceReady: isWhatsappLogin,
      }));
    });

    newSocket.on("login_started", (data) => {
      setIsLoggingIn(true);
      setWhatsappStatus("STARTING LOGIN");
      setStatusDetails(data.message);
    });

    newSocket.on("qr_code", (data) => {
 
      
      if (!data.qr) {
        console.error("❌ QR code data is missing!");
        return;
      }
      
      const qrDataUrl = `data:image/png;base64,${data.qr}`;
      
      setQrCode(qrDataUrl);
      setLastQrTime(new Date().toISOString());
      setWhatsappStatus("QR READY");
      setStatusDetails("Scan the QR code with WhatsApp to connect");
      setIsLoggingIn(true);
      setQrLoading(false);
      setScanningLoader(false);
      
      console.log("✅ QR Code state updated successfully!");
    });

    newSocket.on("qr_scanning", () => {
      setScanningLoader(true);
      setWhatsappStatus("SCANNING QR");
      setStatusDetails("QR code scan detected, processing...");
    });

    newSocket.on("login_success", (data) => {
      setWhatsappStatus("LOGGED IN");
      setStatusDetails(data.message);
      setQrCode(null);
      setIsLoggedIn(true);
      setIsLoggingIn(false);
      setScanningLoader(false);
      setConnectionInfo((prev) => ({ ...prev, instanceReady: true }));
      toast.success("WhatsApp connected successfully!");
    });

    newSocket.on("qr_timeout", (data) => {
      setWhatsappStatus("QR EXPIRED");
      setStatusDetails(data.message);
      setQrCode(null);
      setIsLoggingIn(false);
      setScanningLoader(false);
      setQrLoading(false);
      toast.error("QR code expired. Please request a new one.");
    });

    newSocket.on("login_timeout", (data) => {
      setWhatsappStatus("LOGIN TIMEOUT");
      setStatusDetails(data.message);
      setQrCode(null);
      setIsLoggingIn(false);
      setScanningLoader(false);
      setQrLoading(false);
      toast.error("Login timeout. Please try again.");
    });

    newSocket.on("auth_failure", (data) => {
      setWhatsappStatus("AUTH FAILED");
      setStatusDetails(data.message);
      setIsLoggedIn(false);
      setIsLoggingIn(false);
      setQrCode(null);
      setScanningLoader(false);
      setQrLoading(false);
      toast.error("Authentication failed");
    });

    newSocket.on("login_error", (data) => {
      setWhatsappStatus("LOGIN ERROR");
      setStatusDetails(data.message);
      setIsLoggingIn(false);
      setQrCode(null);
      setScanningLoader(false);
      setQrLoading(false);
      toast.error("Login error occurred");
    });

    newSocket.on("disconnected", (data) => {
      setWhatsappStatus("DISCONNECTED");
      setStatusDetails(data.message);
      setIsLoggedIn(false);
      setIsLoggingIn(false);
      setQrCode(null);
      setScanningLoader(false);
      setQrLoading(false);
      // toast.warning("WhatsApp disconnected");
    });

    newSocket.on("connection_issue", (data) => {
      setWhatsappStatus("CONNECTION ISSUE");
      setStatusDetails(data.message || "Connection lost, attempting to reconnect...");
      toast.info("Connection issue detected, reconnecting...");
    });

    newSocket.on("logout_detected", (data) => {
      console.log("🚨 Logout detected from backend:", data);
      setWhatsappStatus("LOGGED OUT");
      setStatusDetails(data.message || "Device logged out. Please scan QR code again.");
      setIsLoggedIn(false);
      setIsLoggingIn(false);
      setIsLoggingOut(false);
      setQrCode(null);
      setScanningLoader(false);
      setQrLoading(false);
      setConnectionInfo((prev) => ({ ...prev, instanceReady: false }));
      toast.error("WhatsApp logged out from device. Please scan QR code again.");
    });

    newSocket.on("logging_out", (data) => {
      setIsLoggingOut(true);
      setStatusDetails(data?.message || "Logging out from WhatsApp...");
      toast.info("Logging out...");
    });

    newSocket.on("logout_success", (data) => {
      setIsLoggingOut(false);
      setIsLoggedIn(false);
      toast.success(data?.message || "Logged out successfully");
    });

    newSocket.on("logout_error", (data) => {
      setIsLoggingOut(false);
      toast.error(data?.message || "Failed to logout");
    });

    newSocket.on("restart_instance_started", (data) => {
      console.log(`Restart started for ${data.instanceId}: ${data.message}`);
      setIsLoggedIn(false);
      setIsRestarting(true);
      setConnectionInfo((prev) => ({
        ...prev,
        instanceReady: false,
      }));
    });

    newSocket.on("restart_instance_error", (data) => {
      console.error("Restart error:", data);
      setIsLoggedIn(false);
      toast.error(data?.message || "Failed to restart instance");
    });

    newSocket.on("restart_instance_done", (data) => {
      setIsRestarting(false);
      toast.success(data?.message || "Instance restarted successfully");
    });

    setSocket(newSocket);
    return newSocket;
  };

  const handleRestart = () => {
    if (!socket || !instanceId) return;
    console.log(`Requesting restart for instance: ${instanceId}`);
    socket.emit("restart_instance", { instanceId });
  };

  useEffect(() => {
    fetchInstanceData();
  }, [instanceId]);

  useEffect(() => {
    if (authToken) {
      initializeSocket();
    }
  }, [authToken]);

  // Copy helper
  const copyToClipboard = async (text, label = "Text") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Navigation/Actions
  const handleRequestQr = () => {
    console.log("🔘 Generate QR button clicked!");
    console.log("📊 Socket exists:", !!socket);
    console.log("📊 Socket connected:", socket?.connected);
    console.log("📊 Is logging in:", isLoggingIn);
    console.log("📊 Instance ID:", instanceId);
    
    if (socket && !isLoggingIn) {
      console.log("✅ Emitting 'request_qr' event to backend...");
      setQrLoading(true);
      setScanningLoader(false);
      socket.emit("request_qr", { instanceId });
      setWhatsappStatus("Generating QR...");
      setStatusDetails("Please wait while we generate a new QR code");
    } else {
      console.error("❌ Cannot request QR - socket issue or already logging in");
      toast.error("Unable to request QR code. Please check connection.");
    }
  };

  const handleButtonClick = () => {
    navigate(`/instance/${instanceId}/chatbots`);
  };

  const handleLogout = () => {
    if (socket && isLoggedIn && !isLoggingOut) {
      socket.emit("logout_instance", { instanceId });
    } else {
      toast.error("Unable to logout. Please check connection.");
    }
  };

  const handleSafeModeChange = async (nextValue) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/instances/${instanceId}/safe-mode`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userAccessToken")}`,
          },
          body: JSON.stringify({ safe_mode: nextValue ? 1 : 0 }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        setSafeMode(!!data.data.safe_mode);
        toast.success(data.message || "Safe Mode updated successfully");
      } else {
        setSafeMode((prev) => !prev);
        toast.error(data.error || "Failed to update Safe Mode");
        console.error("Safe Mode Error:", data.error);
      }
    } catch (err) {
      setSafeMode((prev) => !prev);
      console.error("Safe Mode Update Exception:", err);
    }
  };

  return (
    <MessageProvider
      instanceId={instanceId}
      authToken={authToken}
      isLoggedIn={isLoggedIn}
    >
      <TagProvider instanceId={instanceId}>
                  <div className="h-screen bg-white text-gray-900 relative flex flex-col overflow-hidden">
                    {/* Header */}
                    <motion.header
                      initial={{ y: -50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.5 }}
                      className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur-md shadow-sm flex-shrink-0"
                    >
                      <div className="mx-auto px-3">
                        <div className="flex justify-between items-center h-14">
                          {/* Left - Logo and Instance Info */}
                          <motion.div
                            className="flex items-center"
                            whileHover={{ scale: 1.02 }}
                          >
                            <motion.button
                              onClick={() => navigate(-1)}
                              className="mr-3 p-1.5 rounded-lg text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              title="Go Back"
                            >
                              <ArrowLeft size={18} />
                            </motion.button>
                            <img src={logo} alt="Logo" className="h-10 mr-2.5" />
                            <div>
                              <h1 className="text-base font-bold text-gray-900">
                                {instanceName ? instanceName : "BluTec PING"}
                              </h1>
                              <p className="text-xs text-gray-600">
                                Instance : {instanceId}
                              </p>
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    </motion.header>

                    {/* Main content */}
                    <div className="flex-1 flex flex-col overflow-hidden">
                      {/* Main Tabs Section */}
                      <motion.div
                        className="flex-1 flex flex-col rounded-lg shadow-sm border border-gray-200 bg-white overflow-hidden"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        {/* Tab Navigation */}
                        <div className="border-b border-gray-200 bg-white flex-shrink-0">
                          <nav className="flex space-x-4 px-3 overflow-x-auto">
                            <motion.button
                              onClick={() => setActiveTab("configuration")}
                              className={`flex items-center py-3 px-1 border-b-2 font-medium text-xs transition-colors whitespace-nowrap ${
                                activeTab === "configuration"
                                  ? "border-[#01CC54] text-[#01CC54]"
                                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                              }`}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              type="button"
                            >
                              <Settings size={16} className="mr-1.5" />
                              Configuration
                            </motion.button>

                            {isLoggedIn && (
                              <motion.button
                                onClick={() => setActiveTab("messaging")}
                                className={`flex items-center py-3 px-1 border-b-2 font-medium text-xs transition-colors whitespace-nowrap ${
                                  activeTab === "messaging"
                                    ? "border-[#01CC54] text-[#01CC54]"
                                    : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                                }`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="button"
                              >
                                <MessageCircle size={16} className="mr-1.5" />
                                Messaging
                              </motion.button>
                            )}

                          </nav>
                        </div>

                        {/* Tab Content - Scrollable */}
                        <div className="flex-1 overflow-y-auto p-3">
                          <AnimatePresence mode="wait">
                            {activeTab === "configuration" && (
                              <motion.div
                                key="configuration"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.3 }}
                              >
                                 <ConfigurationTab
                                  instanceId={instanceId}
                                  authToken={authToken}
                                  tokenExpiry={tokenExpiry}
                                  username={username}
                                  showToken={showToken}
                                  setShowToken={setShowToken}
                                  instanceName={instanceName}
                                  setInstanceName={setInstanceName}
                                  updateInstanceName={updateInstanceName}
                                  safeMode={safeMode}
                                  setSafeMode={setSafeMode}
                                  handleSafeModeChange={handleSafeModeChange}
                                  socket={socket}
                                  qrCode={qrCode}
                                  whatsappStatus={whatsappStatus}
                                  statusDetails={statusDetails}
                                  isLoggedIn={isLoggedIn}
                                  isLoggingIn={isLoggingIn}
                                  isLoggingOut={isLoggingOut}
                                  lastQrTime={lastQrTime}
                                  connectionInfo={connectionInfo}
                                  qrLoading={qrLoading}
                                  scanningLoader={scanningLoader}
                                  handleRequestQr={handleRequestQr}
                                  handleLogout={handleLogout}
                                  copyToClipboard={copyToClipboard}
                                  knowledgeBase={knowledgeBase}
                                  setKnowledgeBase={setKnowledgeBase}
                                  saveKnowledgeBase={saveKnowledgeBase}
                                  chatbotEnabled={chatbotEnabled}
                                  setChatbotEnabled={setChatbotEnabled}
                                  toggleChatbot={toggleChatbot}
                                  replyGroup={replyGroup}
                                  setReplyGroup={setReplyGroup}
                                  toggleReplyGroup={toggleReplyGroup}
                                  voiceTranscription={voiceTranscription}
                                  setVoiceTranscription={setVoiceTranscription}
                                  toggleVoiceTranscription={
                                    toggleVoiceTranscription
                                  }
                                  language={language}
                                  setLanguage={setLanguage}
                                  updateLanguage={updateLanguage}
                                  role={role}
                                  setRole={setRole}
                                  updateRole={updateRole}
                                  tone={tone}
                                  setTone={setTone}
                                  updateTone={updateTone}
                                  languageOptions={languageOptions}
                                  replyToBoardcastOnly={replyToBoardcastOnly}
                                  setReplyToBoardcastOnly={
                                    setReplyToBoardcastOnly
                                  }
                                  updateReplyToBroadcastOnly={
                                    updateReplyToBroadcastOnly
                                  }
                                  ai_reply_days={ai_reply_days}
                                  setAiReplyDays={setAiReplyDays}
                                  ai_reply_start_time={ai_reply_start_time}
                                  setAiReplyStartTime={setAiReplyStartTime}
                                  ai_reply_end_time={ai_reply_end_time}
                                  setAiReplyEndTime={setAiReplyEndTime}
                                  updateAiReplyScheduleAPI={
                                    updateAiReplyScheduleAPI
                                  }
                                  ai_timezone={ai_timezone}
                                  setAiTimezone={setAiTimezone}
                                  outOfContextReply={outOfContextReply}
                                  setOutOfContextReply={setOutOfContextReply}
                                  updateOutOfContextReply={
                                    updateOutOfContextReply
                                  }
                                  handleRestart={handleRestart}
                                  isRestarting={isRestarting}
                                  tasksAndRules={tasksAndRules}
                                  setTasksAndRules={setTasksAndRules}
                                  updateTasksAndRules={updateTasksAndRules}
                                  webhookUrl={webhookUrl}
                                  setWebhookUrl={setWebhookUrl}
                                  updateWebhookUrl={updateWebhookUrl}
                                  webhookEnabled={webhookEnabled}
                                  setWebhookEnabled={setWebhookEnabled}
                                  updateWebhookEnabled={updateWebhookEnabled}
                                  generateConfigFromWebsite={
                                    generateConfigFromWebsite
                                  }
                                  isGeneratingConfig={isGeneratingConfig}
                                />
                              </motion.div>
                            )}

                            {activeTab === "messaging" && (
                              <motion.div
                                key="messaging"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                              >
                                <MessagingTab
                                  instanceId={instanceId}
                                  authToken={authToken}
                                  isLoggedIn={isLoggedIn}
                                  stats={stats}
                                />
                              </motion.div>
                            )}

                          </AnimatePresence>
                        </div>
                      </motion.div>
                    </div>
                  </div>
          </TagProvider>
    </MessageProvider>
  );
};

// Messaging Tab Component
const MessagingTab = ({ instanceId, authToken, isLoggedIn, stats }) => {
  return (
    <div className="space-y-3 text-gray-900">
      {/* Message Content */}
      <div className="min-h-[300px]">
        <SingleMessage
          instanceId={instanceId}
          authToken={authToken}
          isLoggedIn={isLoggedIn}
        />
      </div>
    </div>
  );
};

export default InstancePage;
